import React, { FC, useContext } from 'react';
import {
  Button,
  ButtonsContainer,
  PredictionsContainer,
  Subtitle,
  ToggleContainer,
  OptionContainer,
  Checkbox,
  NoPredictionsContainer,
} from './Predictions.style';
import { Autocomplete, TextField } from '@mui/material';
import Toggle from 'react-toggle';
import { ISegmentationLabel, ISegmentationPrediction } from 'models/icardio';
import { labelToColor } from '../../../../utilities/labelColors';
import { UserContext } from 'services/contexts';

const autocompleteSx = {
  '& .MuiOutlinedInput-root': {
    padding: '6px 10px',
    fontFamily: 'inherit',
    fontSize: '14px',
    backgroundColor: '#532e63',
    color: 'white',
    borderRadius: '5px',
  },
  '& .MuiAutocomplete-popupIndicator, & .MuiAutocomplete-clearIndicator': {
    color: 'white',
  },
};

interface IProps {
  activeFrameUuid: string | undefined;
  activeSegmentationPredictionClasses: string[];
  editMode: boolean;
  segmentationPredictions: ISegmentationPrediction[];
  showSegmentationPredictions: boolean;
  segmentationLabels: ISegmentationLabel[];
  selectedSource: string | null | undefined;
  setActiveSegmentationPredictionClasses: (value: string[]) => void;
  setActiveTool: (tool: string) => void;
  setShowSegmentationPredictions: (value: (prev: boolean) => boolean) => void;
  setSegmentationLabels: (labels: ISegmentationLabel[]) => void;
  markSegmentationAsDirty: (segmentation: Partial<ISegmentationLabel>) => void;
  setSelectedSource: (source: string | null) => void;
  setShowOtherSegmentations: (value: (prev: boolean) => boolean) => void;
}

const Predictions: FC<IProps> = ({
  activeFrameUuid,
  activeSegmentationPredictionClasses,
  editMode,
  segmentationPredictions,
  showSegmentationPredictions,
  segmentationLabels,
  selectedSource,
  setActiveTool,
  setActiveSegmentationPredictionClasses,
  setShowSegmentationPredictions,
  setSegmentationLabels,
  markSegmentationAsDirty,
  setSelectedSource,
  setShowOtherSegmentations,
}: IProps) => {
  const { user } = useContext(UserContext);

  const sourceNameOptions = Array.from(
    new Set(segmentationPredictions?.map((prediction: any) => prediction.sourceName) ?? [])
  ).map(sourceName => ({
    value: sourceName as string | null,
    label: sourceName === null ? '' : sourceName,
  }));

  const activeSourceOption =
    sourceNameOptions.find(option => option.value === selectedSource) ?? sourceNameOptions[0];
  const activeSource = activeSourceOption?.value ?? null;

  const selectedSourcePredictions = segmentationPredictions.filter(
    prediction => prediction.sourceName === activeSource
  );

  const renderInput = params => <TextField {...params} label="" variant="outlined" />;

  const selectedSourceClassValueOptions: string[] = Array.from(
    new Set(selectedSourcePredictions.map(prediction => prediction.segmentationClassValue))
  ).sort((a, b) => a.localeCompare(b));

  const selectedPredictions = selectedSourcePredictions.filter(prediction =>
    activeSegmentationPredictionClasses.includes(prediction.segmentationClassValue)
  );
  const selectedPredictionsForActiveFrame = selectedPredictions.filter(
    prediction => prediction.frame === activeFrameUuid
  );

  const adoptOneFrameButtonIsDisabled =
    editMode || !showSegmentationPredictions || selectedPredictionsForActiveFrame.length === 0;
  const adoptAllFramesButtonIsDisabled =
    editMode || !showSegmentationPredictions || selectedPredictions.length === 0;

  const handleToggleShowSegmentationPredictions = () => {
    if (editMode) return;
    const next = !showSegmentationPredictions;
    setShowSegmentationPredictions(() => next);

    if (next) {
      setSelectedSource(activeSource);
      // setShowSegmentationReferences(() => false);
      setActiveTool('segmentor');

      // if activeSegmentationClasses is empty, set all possible classes
      if (activeSegmentationPredictionClasses.length === 0) {
        setActiveSegmentationPredictionClasses(selectedSourceClassValueOptions);
      }
    }
  };

  const handleSourceOnChange = (_, newValue) => {
    if (!newValue) return;

    const nextSourceClassValueOptions: string[] = Array.from(
      new Set(
        segmentationPredictions
          .filter(prediction => prediction.sourceName === newValue.value)
          .map(prediction => prediction.segmentationClassValue)
      )
    ).sort((a, b) => a.localeCompare(b));

    setSelectedSource(newValue.value);
    setActiveSegmentationPredictionClasses(nextSourceClassValueOptions);
  };

  const adoptPredictions = (predictions: ISegmentationPrediction[]) => {
    // -------------------------------
    // RULE 1 + RULE 2: Update existing labels or leave untouched
    // -------------------------------
    const updatedExistingLabels = segmentationLabels.map(label => {
      const matchingPred = predictions.find(
        pred =>
          pred.frame === label.frame && pred.segmentationClassValue === label.segmentationClassValue
      );

      if (!matchingPred) {
        // Rule 1: No matching prediction → return unchanged label
        return label;
      }

      // Rule 2: Matching prediction exists → update label fields
      return {
        ...label,
        paths: matchingPred.paths,
        sourceUuid: matchingPred.uuid,
        sourceType: 'SEGMENTATION_PREDICTION',
      };
    });

    // -------------------------------
    // RULE 3: Create new labels from predictions that have no matching labels
    // -------------------------------
    const newLabelsFromPredictions = predictions
      .filter(pred => {
        return !segmentationLabels.some(
          label =>
            label.frame === pred.frame &&
            label.segmentationClassValue === pred.segmentationClassValue
        );
      })
      .map(pred => ({
        frame: pred.frame,
        segmentationClassValue: pred.segmentationClassValue,
        segmentationClass: pred.segmentationClass,
        paths: pred.paths,
        sourceUuid: pred.uuid,
        sourceType: 'SEGMENTATION_PREDICTION',
        isFullyVisible: true,
        userEmail: user.email,
      }));

    const updatedLabels = updatedExistingLabels.filter(
      (label, index) => label !== segmentationLabels[index]
    );

    // -------------------------------
    // Combine all updated + new labels
    // -------------------------------
    const finalLabels = [
      ...updatedExistingLabels,
      ...newLabelsFromPredictions,
    ] as ISegmentationLabel[];

    setSegmentationLabels(finalLabels);
    [...updatedLabels, ...newLabelsFromPredictions].forEach(markSegmentationAsDirty);
    setShowSegmentationPredictions(() => false);
    setShowOtherSegmentations(() => true);
  };

  const handleAdoptOneFrame = () => {
    if (adoptOneFrameButtonIsDisabled) return;
    adoptPredictions(selectedPredictionsForActiveFrame);
  };

  const handleAdoptAll = () => {
    if (adoptAllFramesButtonIsDisabled) return;

    const countsByClass = selectedPredictions.reduce((acc, pred) => {
      acc[pred.segmentationClassValue] = (acc[pred.segmentationClassValue] || 0) + 1;
      return acc;
    }, {});

    const classes = Object.keys(countsByClass).sort((a, b) => a.localeCompare(b));

    const message =
      `Are you sure you want to adopt ALL selected predictions across ALL frames for the following features?\n` +
      classes.map(cls => ` - ${cls} (${countsByClass[cls]})`).join('\n');

    if (!confirm(message)) return;

    adoptPredictions(selectedPredictions);
  };

  const segmentationClassCheckBoxes = selectedSourceClassValueOptions.map(
    (segmentationClass: string) => {
      const handleCheckboxClick = () => {
        if (activeSegmentationPredictionClasses.includes(segmentationClass)) {
          setActiveSegmentationPredictionClasses(
            activeSegmentationPredictionClasses.filter(c => c !== segmentationClass)
          );
        } else {
          setActiveSegmentationPredictionClasses([
            ...activeSegmentationPredictionClasses,
            segmentationClass,
          ]);
        }
      };

      const isSelected = activeSegmentationPredictionClasses.includes(segmentationClass);
      const color = labelToColor(segmentationClass);

      return (
        <OptionContainer key={segmentationClass} onClick={handleCheckboxClick}>
          <Checkbox color={color} checked={isSelected} readOnly />
          {segmentationClass}
        </OptionContainer>
      );
    }
  );

  const noPredictions = selectedSourcePredictions.length === 0;

  const NoPredictions = () => (
    <NoPredictionsContainer>No predictions available for this Dicom</NoPredictionsContainer>
  );

  return (
    <PredictionsContainer>
      {noPredictions ? (
        <NoPredictions />
      ) : (
        <>
          <Subtitle>Model</Subtitle>
          <Autocomplete
            fullWidth
            options={sourceNameOptions}
            defaultValue={activeSourceOption}
            getOptionLabel={option => option.label}
            value={activeSourceOption ?? null}
            onChange={handleSourceOnChange}
            isOptionEqualToValue={(option, value) => option.value === value.value}
            sx={autocompleteSx}
            renderInput={renderInput}
          />
          <Subtitle>Feature</Subtitle>
          {segmentationClassCheckBoxes}
          <ToggleContainer onClick={handleToggleShowSegmentationPredictions}>
            <div style={{ color: '#fff' }}>Show predictions</div>
            <Toggle
              checked={showSegmentationPredictions}
              onClick={handleToggleShowSegmentationPredictions}
              readOnly
            />
          </ToggleContainer>
          <Subtitle>Adopt Visible Predictions</Subtitle>
          <ButtonsContainer>
            <Button
              data-testid="adopt-prediction-button"
              disabled={adoptOneFrameButtonIsDisabled}
              color="darkgreen"
              onClick={handleAdoptOneFrame}
            >
              Adopt one frame
            </Button>
            <Button
              data-testid="adopt-all-predictions-button"
              disabled={adoptAllFramesButtonIsDisabled}
              onClick={handleAdoptAll}
              color="#00008B"
            >
              Adopt all frames
            </Button>
          </ButtonsContainer>
        </>
      )}
    </PredictionsContainer>
  );
};

export default Predictions;
